'use client';

import type { FC } from 'react';
import type { FooterLinkSection } from '@/ui/components/Footer/footer-types';
import {
  MIND_OASIS_APP_STORE_ID,
  MIND_OASIS_PLAY_STORE_ID,
} from '@/core/constants';
import AppStoreButton from '@/ui/components/AppStoreButton/AppStoreButton';
import FooterLink from '@/ui/components/Footer/FooterLink/FooterLink';
import PlayStoreButton from '@/ui/components/PlayStoreButton/PlayStoreButton';
import Typography from '@/ui/components/Typography/Typography';
import { useFeatureFlags } from '@/ui/context/feature-flags';

import $ from './Footer.module.scss';

const CUSTOM_ACTION_HANDLERS: {
  manageCookies: () => void;
} = {
  manageCookies: () => {
    if (typeof window !== 'undefined' && window.OneTrust) {
      window.OneTrust.ToggleInfoDisplay();
    }
  },
};

type FooterNavigationProps = {
  /** Link sections from Uniform composition. Renders nothing when empty or undefined. */
  sections?: FooterLinkSection[] | null;
};

const AppDownloadButtons: FC = () => (
  <>
    <AppStoreButton appId={MIND_OASIS_APP_STORE_ID} />
    <PlayStoreButton appId={MIND_OASIS_PLAY_STORE_ID} />
  </>
);

const FooterNavigation: FC<FooterNavigationProps> = ({ sections }) => {
  const { isEnabled } = useFeatureFlags();
  const sectionsToRender =
    Array.isArray(sections) && sections.length > 0 ? sections : null;

  if (!sectionsToRender) {
    return null;
  }

  const lastSectionIndex = sectionsToRender.length - 1;

  return (
    <nav aria-label="Footer navigation" className={$.footerNavigation}>
      {sectionsToRender.map((section, sectionIndex) => (
        <div
          key={section.title || `section-${sectionIndex}`}
          className={$.navigationList}
        >
          <Typography variant="bodyMedium" weight="medium">
            {section.title}
          </Typography>
          <ul className={$.footerLinkList}>
            {section.items
              .filter(item => !item.featureFlag || isEnabled(item.featureFlag))
              .map((item, itemIndex) => {
                const isCustomAction = item.actionType === 'manageCookies';
                const href =
                  (item.actionType === 'link'
                    ? item.href
                    : (item.href ?? '#onetrust')) ?? '#';
                const onClick = isCustomAction
                  ? CUSTOM_ACTION_HANDLERS.manageCookies
                  : undefined;
                const linkAccessibleName =
                  item.ariaLabel ??
                  item.label ??
                  (isCustomAction ? 'Cookie preferences' : undefined);
                return (
                  <li key={item.id ?? item.href ?? itemIndex} id={item.id}>
                    <FooterLink
                      href={href}
                      ariaLabel={linkAccessibleName}
                      trackTitle={item.label}
                      isExternal={item.isExternal}
                      onClick={onClick}
                    >
                      {item.label}
                    </FooterLink>
                  </li>
                );
              })}
          </ul>
          {sectionIndex === lastSectionIndex && (
            <div className={$.appDownloadButtonsInSection}>
              <AppDownloadButtons />
            </div>
          )}
        </div>
      ))}
      <div className={$.appDownloadButtonsMobileRow}>
        <AppDownloadButtons />
      </div>
    </nav>
  );
};

export default FooterNavigation;
